import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {FormControl, FormGroup} from "@angular/forms";
import * as Validation from "../../../../shared/constants/validation.constants";
import {CommonService} from "../../../../shared/services/common.service";
import {debounceTime, Subject, takeUntil} from "rxjs";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import {
  adultUsernameFilter,
  checkParticipantHasAssociatedLar,
  participantUsernameFilter
} from "../../core/invite.selectors";
import {
  FetchAdultUsernameFilterRequest,
  FetchParticipantUsernameFilterRequest,
  HasAssociatedLarRequest
} from "../../core/invite.actions";
import {Invite} from "../../services/invite";
import {Filter} from "../../../../../models/filter";
import {profile} from "../../../../shared/core/shared.selectors";
import {roles} from "../../../../shared/constants/common.constants";

@Component({
  selector: "app-existing-user",
  templateUrl: "./existing-user.component.html",
  styleUrls: ["./existing-user.component.css"]
})
export class ExistingUserComponent implements OnInit, OnDestroy {
  @Input() index: number;
  @Input() clientId: number;
  @Input() isParticipant: boolean = false;
  @Input() form: FormGroup = new FormGroup({});
  @Input() errorMessages: any = {};
  @Input() errorMessageMap: any = {};
  @Output() larChange = new EventEmitter<number>();
  usernameFilter: Filter = {
    list: [],
    total: 0,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  };
  validation = Validation;
  userEmailId: any;
  userRole: any;
  selectedUsername: any;
  enableUsernameClearSelection: boolean = true;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private invite: Invite,
  ) {
  }

  ngOnInit() {

    this.store.select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.userRole = data?.role;
      })

    this.invite.isVerifyDisabled.next(true);
    if (this.isParticipant) {
      this.store.select(participantUsernameFilter)
        .pipe(takeUntil(this.onDestroy)).subscribe(data => {
        const list = data?.list?.filter((item) => item.key !== this.selectedUsername?.key);
        this.usernameFilter.list.push(...(list || []));
        this.usernameFilter.loading = data?.loading;
        this.usernameFilter.hasNext = data?.hasNext;
        this.usernameFilter.pageNumber = data?.pageNumber;
        this.usernameFilter.limit = data?.limit;
      });
    } else {
      this.store.select(adultUsernameFilter).pipe(takeUntil(this.onDestroy))
        .subscribe(data => {
          const list = data?.list?.filter((item) => item.key !== this.selectedUsername?.key);
          this.usernameFilter.list.push(...(list || []));
          this.usernameFilter.loading = data?.loading;
          this.usernameFilter.hasNext = data?.hasNext;
          this.usernameFilter.pageNumber = data?.pageNumber;
          this.usernameFilter.limit = data?.limit;
        });
    }

    this.form.controls["emailId"].valueChanges.pipe(takeUntil(this.onDestroy))
      .pipe(debounceTime(2000)).subscribe(emailId => {
      this.usernameFilter.list = [];
      this.form.controls["userId"].setValue(null);
      this.enableUsernameClearSelection = true;
      this.usernameFilter.pageNumber = 1;
      if (new RegExp(this.validation.email.regex).test(emailId)) {
        this.userEmailId = emailId;
        this.fetchUsername(emailId);
      }
    });

    this.form.controls["userId"].valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(userId => {
        if (this.form.controls["userId"].dirty) {
          if (userId) {
            if (this.isParticipant) {
              this.store.dispatch(HasAssociatedLarRequest({payload: {userId}}));
            }
            this.selectedUsername = this.usernameFilter.list.find((item) => item.key === userId);
          } else {
            if (this.isParticipant) {
              this.invite.isExpandAdultSection.next(false)
            }
          }
        }
      });

    this.form.controls["searchUsername"].valueChanges.pipe(debounceTime(1000), takeUntil(this.onDestroy))
      .subscribe(data => {
        this.usernameFilter.pageNumber = 1;
        this.usernameFilter.list = [];
        if (this.selectedUsername) {
          this.usernameFilter.list.push(this.selectedUsername);
        }
        this.fetchUsername(this.userEmailId);
      });

    this.store.select(checkParticipantHasAssociatedLar)
      .pipe(takeUntil(this.onDestroy)).subscribe(data => {
      this.invite.hasAssociatedLar.next(data?.hasAssociatedLar);
      if (!this.isParticipant) {
        if (!data?.hasAssociatedLar) {
          this.form.addControl(
            "isLar",
            new FormControl({value: false, disabled: false})
          );
        } else {
          this.form.removeControl("isLar");
        }
      }
    });

    this.form.controls["isLar"]?.valueChanges.pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.larChange.emit(this.index);
      });

    this.invite.resetExistingUserForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.form.reset({}, {emitEvent: false});
        }
      });

    this.invite.disableForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.form.disable();
        }
      });
  }

  fetchUsername(emailId?) {
    const payload: any = {
      pageNumber: this.usernameFilter?.pageNumber,
      limit: this.usernameFilter?.limit,
      ...(this.userRole === roles.superAdmin && {clientId: this.clientId})
    };

    if (this.form.controls["searchUsername"]?.value?.trim()) {
      payload.username = this.form.controls["searchUsername"]?.value;
    }
    if (this.isParticipant) {
      this.store.dispatch(FetchParticipantUsernameFilterRequest({payload: {emailId, ...payload}}));
    } else {
      this.store.dispatch(FetchAdultUsernameFilterRequest({payload: {emailId, ...payload}}));
    }
  }

  usernamesOnNextPage() {
    this.usernameFilter.pageNumber = this.usernameFilter.pageNumber + 1;
    this.fetchUsername(this.userEmailId);
  }

  checkForErrors(currentField?) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
